/**
 * /reports — the field report, straight from the device's own log.
 *
 * ★★ What this page used to be: six KPI cards ("舰队在线率 96.8% ↑0.5%",
 *    "MTBI 187h ↑12h", "总覆盖面积 8,420㎡"), a per-terminal task table for ten
 *    terminals that do not exist, a seven-day MTBI curve and a six-week coverage
 *    curve — every number a literal in src/mocks/reportData.ts, complete with
 *    trend arrows. Trend arrows are the tell: they imply someone measured the same
 *    thing yesterday.
 *
 * Where the numbers come from now: the dog's own missions.db, summarised by
 * `mission.Report` on the device and pushed to the hub as-is (internal/reportsync).
 * NOTHING is recomputed in the cloud or in this component — those numbers get
 * defended in a paper, and a second implementation of "success rate" would drift
 * from the first. This page's whole job is to display them and to keep every
 * denominator attached.
 *
 * ★ The `gaps` list is displayed prominently rather than hidden: the device says
 *   what is still missing for a credible write-up (a second site, more actionable
 *   missions, a longer span). A report page that only shows what it has is how a
 *   column stays empty until submission day.
 */
import { useState } from 'react';
import { useReports } from '@/hooks/useHubData';
import { NoDataSource, LoadError } from '@/components/feature/DataSource';
import type { FieldReport } from '@/lib/hubClient';

type Window = 'all' | 'd30';

export default function ReportsPage() {
  const { reports, loading, error, unavailable, refetch } = useReports();
  const [win, setWin] = useState<Window>('all');
  const [deviceId, setDeviceId] = useState<string | null>(null);

  if (error) return <div className="p-4 lg:p-6"><LoadError error={error} onRetry={refetch} /></div>;
  if (unavailable) {
    return (
      <div className="p-4 lg:p-6">
        <NoDataSource title="hub 没有报表存储" reason={unavailable} />
      </div>
    );
  }
  if (loading && !reports) {
    return <div className="p-4 lg:p-6"><p className="text-xs text-foreground-400">读取现场报表…</p></div>;
  }
  if (!reports || reports.length === 0) {
    return (
      <div className="p-4 lg:p-6">
        <NoDataSource
          title="还没有设备推过现场报表"
          reason="报表由设备侧的现场日志(missions.db)汇总后每 10 分钟推一次。设备没开日志、或还没跑过任务时, 这里就是空的 —— 而不是一组好看的默认值。"
        />
      </div>
    );
  }

  const active = reports.find((r) => r.deviceId === deviceId) ?? reports[0];
  const rep: FieldReport | undefined = win === 'all' ? active.payload?.all : active.payload?.d30;
  const ageMin = Math.max(0, Math.round((Date.now() / 1000 - active.updatedAt) / 60));

  if (!rep) {
    return (
      <div className="p-4 lg:p-6">
        <LoadError error={`${active.deviceId} 推来的报表缺 ${win} 窗口 —— 版本不匹配?`} onRetry={refetch} />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4 animate-fade-in-up">
        <div>
          <h1 className="text-lg font-bold text-foreground-900 font-heading mb-1">报表 · 现场日志</h1>
          <p className="text-xs text-foreground-500">
            数字全部由设备侧 <span className="font-mono">mission.Report</span> 从 missions.db 算出后推上来,
            云端不重算。{active.deviceId} · 报表 {ageMin} 分钟前
          </p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {reports.length > 1 && reports.map((r) => (
            <button key={r.deviceId} onClick={() => setDeviceId(r.deviceId)}
              className={`px-2.5 py-1 rounded-md text-[11px] font-mono cursor-pointer transition-all ${
                r.deviceId === active.deviceId ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-500'}`}>
              {r.deviceId}
            </button>
          ))}
          <div className="flex items-center gap-0.5 bg-background-100 rounded-lg p-0.5 ml-1">
            {([{ k: 'all' as Window, label: '全时段' }, { k: 'd30' as Window, label: '近 30 天' }]).map((t) => (
              <button key={t.k} onClick={() => setWin(t.k)}
                className={`px-2.5 py-1.5 text-xs font-medium rounded-md cursor-pointer transition-all ${
                  win === t.k ? 'bg-background-50 text-foreground-800 border border-background-200/70 shadow-sm' : 'text-foreground-500'}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 还缺什么 —— 设备自己列的。放在最上面, 因为报表最容易骗人的方式是只显示已有的。 */}
      {rep.gaps && rep.gaps.length > 0 && (
        <div className="mb-4 px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200/70 animate-fade-in-up">
          <p className="text-[11px] text-amber-800 leading-relaxed">
            <strong className="font-semibold">离一份能交的现场报告还差</strong>
            <span className="text-amber-700/90">(设备自己列的, 不是本页判断的)</span>:
            {rep.gaps.map((g, i) => <span key={i} className="ml-1.5">· {g}</span>)}
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4 animate-fade-in-up" style={{ animationDelay: '0.06s' }}>
        <KPI label="任务成功率" value={pct(rep.successRate)}
          sub={`分母 = 成功 ${rep.success} + 失败 ${rep.failed} + 没找到 ${rep.notfound}(取消不计)`} />
        <KPI label="MTBI" value={rep.mtbiHours > 0 ? `${rep.mtbiHours.toFixed(1)} h` : '—'}
          sub={`${rep.interventions} 次干预 / 观测窗 ${rep.spanHours.toFixed(0)} h(墙钟, 非通电时长)`} />
        <KPI label="指令" value={`${rep.missions}`}
          sub={`其中真正到机器人的 ${rep.actionable} 条(闲聊不算)`} />
        <KPI label="行走距离" value={rep.distanceKm > 0 ? `${rep.distanceKm.toFixed(2)} km` : '—'}
          sub={`${rep.deployments} 次上电 · 场地 ${rep.sites?.length ?? 0} 个`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 animate-fade-in-up" style={{ animationDelay: '0.12s' }}>
        <Panel title="失败归因(按层)" hint="unknown 桶大不是日志的 bug, 是关于这套栈的一个发现 —— 所以不隐藏">
          <Bars data={rep.failuresByLayer} empty="这个窗口里没有归因过的失败" />
        </Panel>

        <Panel title="人工干预(按严重度)" hint="auto=supervisor 自己重拉的也算, 它们同样打断了自主运行">
          <Bars data={rep.interventionsBySeverity} empty="这个窗口里没有记录到干预" />
        </Panel>

        <Panel title="任务去向" hint="每条指令最后落在哪 —— 加起来应当等于指令总数">
          <Bars data={{
            成功: rep.success, 失败: rep.failed, 取消: rep.cancelled,
            没找到: rep.notfound, 中止: rep.aborted, 未理会: rep.ignored, 仍在跑: rep.open,
          }} empty="没有指令" />
        </Panel>

        <Panel title="记忆写入门控" hint="位姿不新鲜时写入的那些, 正是'位姿门'那条实验数据">
          {rep.memWrites === 0 ? (
            <p className="text-[11px] text-foreground-400 py-4 text-center">这个窗口里没有记忆写入</p>
          ) : (
            <>
              <Bars data={rep.memWritesByGate} empty="没有分门别类的记录" />
              <p className="text-[11px] text-foreground-500 mt-2">
                共 {rep.memWrites} 次写入, 其中 <span className="font-mono text-amber-600">{rep.memWritesStale}</span> 次
                发生在位姿【不新鲜】时。
              </p>
            </>
          )}
        </Panel>
      </div>

      <p className="text-[11px] text-foreground-400 leading-relaxed mt-4">
        观测窗 {fmtDate(rep.windowStart)} ~ {fmtDate(rep.windowEnd)}(共 {rep.spanHours.toFixed(0)} 小时墙钟)。
        {(rep.clockSkewedDeployments ?? 0) > 0 && (
          <> 其中 <span className="font-mono">{rep.clockSkewedDeployments}</span> 次上电的时间戳不可用
          (Jetson 没有 RTC, NTP 同步前开机会盖成 1970)——它们照常计入各项计数, 只是不参与时间窗计算。</>
        )}
        {' '}健康快照 {rep.healthSnapshots} 条, 干净关机 {rep.cleanShutdowns}/{rep.deployments} 次。
        {'覆盖率增长曲线这一栏被删了:它需要按时间存档的覆盖数据, 而现在只有"当前这张图看过多少格"(见地图页), 没有历史。'}
      </p>
    </div>
  );
}

function KPI({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-3">
      <div className="text-[10px] text-foreground-500 mb-1">{label}</div>
      <div className="text-xl font-bold text-foreground-900 font-mono">{value}</div>
      {/* 分母永远跟着比率走(§9b:任何比率必须先说清分母的来历)。 */}
      <div className="text-[10px] text-foreground-400 mt-1 leading-relaxed">{sub}</div>
    </div>
  );
}

function Panel({ title, hint, children }: { title: string; hint: string; children: React.ReactNode }) {
  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
      <h3 className="text-xs font-bold text-foreground-800 mb-0.5">{title}</h3>
      <p className="text-[10px] text-foreground-400 mb-3">{hint}</p>
      {children}
    </div>
  );
}

/** 横向条形。没有数据时说"没有", 不画一根 0 长度的条冒充有。 */
function Bars({ data, empty }: { data: Record<string, number> | null | undefined; empty: string }) {
  const entries = Object.entries(data ?? {}).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return <p className="text-[11px] text-foreground-400 py-4 text-center">{empty}</p>;
  const max = Math.max(...entries.map(([, v]) => v));
  return (
    <div className="space-y-1.5">
      {entries.map(([k, v]) => (
        <div key={k} className="flex items-center gap-2">
          <span className="w-24 shrink-0 text-[11px] text-foreground-600 truncate" title={k}>{k}</span>
          <div className="flex-1 h-2 bg-background-200/60 rounded-full overflow-hidden">
            <div className="h-full bg-primary-500 rounded-full" style={{ width: `${(v / max) * 100}%` }} />
          </div>
          <span className="w-10 text-right text-[11px] font-mono text-foreground-700">{v}</span>
        </div>
      ))}
    </div>
  );
}

const pct = (v: number) => (v > 0 || v === 0 ? `${(v * 100).toFixed(1)}%` : '—');
const fmtDate = (ms: number) => (ms > 0 ? new Date(ms).toLocaleString('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
